// src/services/llmService.js
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL;

class LLMService {
  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      timeout: 30000,
    });

    // 🚨 FIX: Time-based override for the API key check
    // This stores a timestamp. If current time < expiry, we assume keys exist.
    // This is robust against React Strict Mode double-renders.
    this.overrideExpiry = 0;

    // Automatically attach auth token from sessionStorage
    this.client.interceptors.request.use(
      (config) => {
        const token = sessionStorage.getItem('token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Handle 401 with token refresh and retry
    this.client.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;
        
        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;
          
          try {
            // Try to refresh token
            const newToken = await this.refreshToken();
            if (newToken) {
              // Update the failed request with new token
              originalRequest.headers.Authorization = `Bearer ${newToken}`;
              return this.client(originalRequest);
            }
          } catch (refreshError) {
            // Token refresh failed, logout
            this.handleLogout();
            return Promise.reject(new Error('Authentication failed. Please login again.'));
          }
        }
        
        return Promise.reject(error);
      }
    );
  }

  // Token refresh logic
  isRefreshing = false;
  failedQueue = [];

  async refreshToken() {
    if (this.isRefreshing) {
      return new Promise((resolve, reject) => {
        this.failedQueue.push({ resolve, reject });
      });
    }

    this.isRefreshing = true;
    
    try {
      const refreshToken = sessionStorage.getItem('refreshToken');
      if (!refreshToken) {
        throw new Error('No refresh token available');
      }
      
      const response = await axios.post(`${API_BASE_URL}/auth/refresh`, {
        refreshToken
      }, {
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      if (response.data.accessToken) {
        const newToken = response.data.accessToken;
        sessionStorage.setItem('token', newToken);
        
        // Process any queued requests
        this.failedQueue.forEach(promise => promise.resolve(newToken));
        this.failedQueue = [];
        
        return newToken;
      } else {
        throw new Error('Token refresh failed');
      }
    } catch (error) {
      console.error('❌ Token refresh failed:', error);
      
      this.failedQueue.forEach(promise => promise.reject(error));
      this.failedQueue = [];
      
      return null;
    } finally {
      this.isRefreshing = false;
    }
  }

  // Centralized logout handler
  handleLogout() {
    console.log('🔒 Authentication failed, logging out...');
    
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('refreshToken');
    
    if (window.location.pathname !== '/login') {
      window.location.href = '/login';
    }
  }

  /** * ✅ NEW: Call this from Signup.js when a user is created.
   * We give the user a 10-second "grace period" where we assume they have keys.
   * This solves race conditions and React Strict Mode double-checks.
   */
  setHasKeyOverride(status) {
    if (status) {
      console.log('🔑 Setting temporary API key override (valid for 10s)');
      // Set expiry to 10 seconds from now
      this.overrideExpiry = Date.now() + 10000;
    } else {
      this.overrideExpiry = 0;
    }
  }

  /** ✅ Check if user has at least one API key */
  async checkApiKeyRequirement() {
    // 1. Check if we are within the override window
    if (Date.now() < this.overrideExpiry) {
      console.log('⚡ Using temporary time-based override for API key check');
      // We do NOT reset it here. We let it expire naturally.
      return { success: true, hasApiKeys: true };
    }

    // 2. Otherwise, check with the server normally
    try {
      const { data } = await this.client.get('/llminput/check-requirement');
      if (!data.success) throw new Error(data.error || 'Failed to check API key requirement');
      return data;
    } catch (error) {
      console.error('❌ Failed to check API key requirement:', error);
      throw new Error(error.response?.data?.error || 'Failed to check API key requirement');
    }
  }

  /** ✅ Get all available LLMs */
  async getAvailableLLMs() {
    try {
      const { data } = await this.client.get('/llminput/available-llms');
      if (!data.success) throw new Error(data.error || 'Failed to fetch available LLMs');
      return data;
    } catch (error) {
      console.error('❌ Failed to fetch available LLMs:', error);
      throw new Error(error.response?.data?.error || 'Failed to fetch available LLMs');
    }
  }

  /** ✅ Validate and save an API key */
  async validateApiKey(llmId, apiKey, customName = '') {
    try {
      const { data } = await this.client.post('/llminput/validate-api-key', {
        llmId,
        apiKey,
        customName,
      });
      if (!data.success) throw new Error(data.error || 'Validation failed');
      return data;
    } catch (error) {
      console.error('❌ API key validation failed:', error);
      throw new Error(error.response?.data?.error || 'Failed to validate API key');
    }
  }

  /** ✅ Fetch user's configured LLMs */
  async getUserLLMs() {
    try {
      const { data } = await this.client.get('/llminput/user-llms');
      if (!data.success) throw new Error(data.error || 'Failed to fetch configured LLMs');
      return data;
    } catch (error) {
      console.error('❌ Failed to fetch user LLMs:', error);
      throw new Error(error.response?.data?.error || 'Failed to fetch configured LLMs');
    }
  }

  /** ✅ Set default LLM */
  async setDefaultLLM(llmId) {
    try {
      const { data } = await this.client.put('/llminput/set-default', { llmId });
      return data;
    } catch (error) {
      console.error('❌ Failed to set default LLM:', error);
      throw new Error(error.response?.data?.error || 'Failed to set default LLM');
    }
  }

  /** ✅ Remove a configured LLM */
  async removeLLM(llmId) {
    try {
      const { data } = await this.client.delete(`/llminput/${llmId}`);
      return data;
    } catch (error) {
      console.error('❌ Failed to remove LLM:', error);
      throw new Error(error.response?.data?.error || 'Failed to remove LLM');
    }
  }
}

export default new LLMService();